// src/pages/Game/GamePlay.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import API from "../../api";

const DEFAULT_TIMER = 20;

// -------- Helpers ----------
const getRank = (xp) => {
  if (xp >= 1000) return "Diamond";
  if (xp >= 500) return "Gold";
  if (xp >= 200) return "Silver";
  return "Bronze";
};

const badgeCheck = ({ totalCorrect, bestStreak, fastestAnswerMs, xp }) => {
  const badges = [];
  if (totalCorrect >= 10) badges.push("Quiz Initiate");
  if (totalCorrect >= 25) badges.push("Quiz Master");
  if (bestStreak >= 5) badges.push("Sharp Shooter");
  if (fastestAnswerMs !== null && fastestAnswerMs <= 2000)
    badges.push("Speed King");
  if (xp >= 500) badges.push("XP Grinder");
  return badges;
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const randomPowerUp = () => {
  const pool = [
    { key: "double", label: "Double Points (this question)" },
    { key: "freeze", label: "Freeze Timer (+5s)" },
    { key: "second", label: "Second Chance (ignore next wrong)" },
  ];
  return pool[Math.floor(Math.random() * pool.length)];
};

// ========================================
//              MAIN COMPONENT
// ========================================
export default function GamePlay() {
  const { testId } = useParams();
 // 🟢 GET TEST ID FROM URL

  // ------------------- Test + Questions -------------------
  const [loading, setLoading] = useState(true);
  const [testTitle, setTestTitle] = useState("");
  const [allQuestions, setAllQuestions] = useState([]);

  useEffect(() => {
    const loadTest = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const res = await API.get(`/publictests/test/${testId}`, { headers });

        setTestTitle(res.data.title || "Quiz Game");

       const formatted = res.data.questions.map((q) => ({
  questionText:
    typeof q.question === "object"
      ? q.question.text
      : q.question || q.text || "",

  questionMedia:
    typeof q.question === "object" ? q.question.media : null,

  type: q.type?.toUpperCase() || "MCQ",

  options: Array.isArray(q.options)
    ? q.options.map(o =>
        typeof o === "object" ? o : { text: o, media: null }
      )
    : q.choices
    ? q.choices.map(o =>
        typeof o === "object" ? o : { text: o, media: null }
      )
    : [],

  answer:
    typeof q.correctAnswer === "object"
      ? q.correctAnswer.text
      : q.correctAnswer || q.answer || q.correct || "",
}));


        setAllQuestions(shuffle(formatted));
      } catch (err) {
        console.error("Failed to load questions", err);
      }
      setLoading(false);
    };

    loadTest();
  }, [testId]);

  // ------------------- Profile -------------------
  const [avatar, setAvatar] = useState("🦊");
  const [xp, setXp] = useState(0);
  const [badges, setBadges] = useState([]);
  const [rank, setRank] = useState("Bronze");

  useEffect(() => {
    const raw = localStorage.getItem("game_profile");
    if (!raw) return;
    try {
      const p = JSON.parse(raw);
      setAvatar(p.avatar);
      setXp(p.xp);
      setBadges(p.badges);
      setRank(getRank(p.xp));
    } catch {}
  }, []);

  const saveProfile = (p) =>
    localStorage.setItem("game_profile", JSON.stringify(p));

  // ------------------- Game State -------------------
  const [idx, setIdx] = useState(0);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [timeLeft, setTimeLeft] = useState(DEFAULT_TIMER);
  const [bomb, setBomb] = useState(false);
  const [finished, setFinished] = useState(false);

  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [level, setLevel] = useState(1);
  const [fastestAnswerMs, setFastestAnswerMs] = useState(null);
  const questionStart = useRef(Date.now());

  // ------------------- Lifelines -------------------
  const [lifelines, setLifelines] = useState({
    fifty: 1,
    skip: 1,
    hint: 1,
  });
  const [hintText, setHintText] = useState("");

  // ------------------- Powerups -------------------
  const [activePower, setActivePower] = useState(null);
  const [availablePower, setAvailablePower] = useState(null);
  const [powerCharges, setPowerCharges] = useState({ drawCount: 0 });

  // ------------------- Init First Question -------------------
  useEffect(() => {
    if (allQuestions.length > 0) {
      const q = allQuestions[0];
      setVisibleOptions(q.type === "MCQ" ? shuffle(q.options) : []);
      questionStart.current = Date.now();
    }
  }, [allQuestions]);

  // ------------------- Timer -------------------
  useEffect(() => {
    if (finished || loading) return;

    const t = setInterval(() => {
      setTimeLeft((sec) => {
        if (sec <= 1) {
          setBomb(true);

          setTimeout(() => {
            setBomb(false);
            handleAnswer(null, { dueToTimeout: true });
          }, 400);

          return 0;
        }
        return sec - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [idx, finished, loading]);

  // ------------------- Freeze Timer Power -------------------
  useEffect(() => {
    if (activePower?.key === "freeze") {
      setTimeLeft((t) => t + 5);
      setActivePower(null);
    }
  }, [activePower]);

  const currentQ = allQuestions[idx];

  // ------------------- Next Question -------------------
  const nextQuestion = () => {
    const next = idx + 1;

    if (next >= allQuestions.length) return finishGame();

    setIdx(next);
    setTimeLeft(DEFAULT_TIMER);
    setAvailablePower(null);
    setHintText("");

    const q = allQuestions[next];
    setVisibleOptions(q.type === "MCQ" ? shuffle(q.options) : []);
    questionStart.current = Date.now();

    // allow 1 power per 3 questions
    setPowerCharges((c) => ({
      drawCount: (next + 1) % 3 === 0 ? 0 : c.drawCount,
    }));
  };

  // ------------------- Finish -------------------
  const finishGame = () => {
    setFinished(true);

    const sessionXP = points;
    const newXP = xp + sessionXP;

    const newBadges = badgeCheck({
      totalCorrect,
      bestStreak,
      fastestAnswerMs,
      xp: newXP,
    });

    const merged = Array.from(new Set([...badges, ...newBadges]));

    setXp(newXP);
    setBadges(merged);
    setRank(getRank(newXP));

    saveProfile({ avatar, xp: newXP, badges: merged });
  };

  // ------------------- Answer Handler -------------------
  const handleAnswer = (selected, { dueToTimeout = false } = {}) => {
    const time = Date.now() - questionStart.current;
    setFastestAnswerMs((prev) => (prev ? Math.min(prev, time) : time));

    const normalize = (v) =>
      v === null || v === undefined ? "" : String(v).trim().toLowerCase();

    const correct =
      !dueToTimeout &&
      normalize(selected?.text ?? selected) === normalize(currentQ.answer ?? "");

    // second chance
    if (!correct && activePower?.key === "second") {
      setActivePower(null);
      return nextQuestion();
    }

    if (correct) {
      const base = 10;
      const streakBonus = streak >= 2 ? 2 : 0;
      const multiplier = activePower?.key === "double" ? 2 : 1;

      const add = (base + streakBonus) * multiplier;
      setPoints((p) => p + add);

      const newStreak = streak + 1;
      setStreak(newStreak);
      setBestStreak((b) => Math.max(b, newStreak));
      setTotalCorrect((c) => c + 1);
    } else {
      setPoints((p) => Math.max(0, p - 5));
      setStreak(0);
    }

    if (activePower?.key === "double") setActivePower(null);

    nextQuestion();
  };

  // ---------------- Lifelines ----------------
  const useFifty = () => {
    if (lifelines.fifty <= 0 || currentQ.type !== "MCQ") return;

    const wrong = visibleOptions.filter((o) => o.text !== currentQ.answer);
    const removeTwo = shuffle(wrong).slice(0, 2);

    setVisibleOptions((v) => v.filter((o) => !removeTwo.includes(o)));
    setLifelines((l) => ({ ...l, fifty: l.fifty - 1 }));
  };

  const useSkip = () => {
    if (lifelines.skip <= 0) return;
    setLifelines((l) => ({ ...l, skip: l.skip - 1 }));
    nextQuestion();
  };

  const useHint = () => {
    if (lifelines.hint <= 0) return;
    const ans = String(currentQ.answer);
    setHintText(`Hint: starts with "${ans.slice(0, 2)}"`);
    setLifelines((l) => ({ ...l, hint: l.hint - 1 }));
  };

  // ---------------- Power-Ups ----------------
  const canDrawPower =
    (idx + 1) % 3 === 0 && powerCharges.drawCount === 0 && !availablePower;

  const drawPower = () => {
    if (!canDrawPower) return;
    setAvailablePower(randomPowerUp());
    setPowerCharges({ drawCount: 1 });
  };

  const activatePower = () => {
    if (!availablePower) return;
    setActivePower(availablePower);
    setAvailablePower(null);
  };

  // ===================================================
  //                   UI
  // ===================================================
  if (loading)
    return <p className="text-center mt-10">Loading quiz...</p>;

  if (!currentQ)
    return (
      <div className="text-center p-10">
        <h2>No questions in this test.</h2>
        <Link to="/game" className="text-blue-600 underline">
          Choose another test
        </Link>
      </div>
    );

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* === TOP STATUS BAR === */}
      <div className="flex justify-between mb-4 items-center">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{avatar}</span>
          <div>
            <div className="font-bold">{testTitle}</div>
            <div className="text-sm text-gray-600">
              XP: {xp} • Level: {level}
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <div
            className={`px-3 py-1 rounded text-white ${
              timeLeft <= 5 ? "bg-red-600 animate-pulse" : "bg-gray-800"
            }`}
          >
            ⏱ {timeLeft}s
          </div>
          <div className="px-3 py-1 bg-green-600 text-white rounded">
            Points: {points}
          </div>
          <div className="px-3 py-1 bg-yellow-500 text-white rounded">
            Streak: {streak}
          </div>
        </div>
      </div>

      {/* === QUESTION CARD === */}
      <div
        className={`bg-white p-5 shadow rounded-2xl ${
          bomb ? "animate-[wiggle_0.4s_ease-in-out]" : ""
        }`}
      >
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <span>
            Q {idx + 1} / {allQuestions.length}
          </span>
          <span>
            Correct: {totalCorrect} • Best: {bestStreak}
          </span>
        </div>

        <h2 className="text-xl font-semibold mb-4">{currentQ.questionText}</h2>
        {currentQ.questionMedia && (
  <img
    src={currentQ.questionMedia}
    className="mt-3 max-h-60 rounded"
    alt=""
  />
)}

        {/* MCQ */}
        {currentQ.type === "MCQ" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {visibleOptions.map((opt, i) => (
  <button
    key={i}
    onClick={() => handleAnswer(opt.text)}
    className="text-left border px-4 py-3 rounded-xl hover:shadow transition"
  >
    <div>{opt.text}</div>

    {opt.media && (
      <img
        src={opt.media}
        className="mt-2 max-h-32 rounded"
        alt=""
      />
    )}
  </button>
))}
          </div>
        )}

        {/* TRUE / FALSE or INPUT */}
        {currentQ.type !== "MCQ" && (
          <NonMcqAnswer onSubmit={(txt) => handleAnswer(txt)} />
        )}

        {hintText && (
          <p className="text-sm text-indigo-600 mt-3">{hintText}</p>
        )}
      </div>

      {/* === CONTROLS === */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        {/* Lifelines */}
        <div className="bg-white p-4 rounded-2xl shadow">
          <div className="font-semibold mb-2">🎗 Lifelines</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={useFifty}
              disabled={lifelines.fifty <= 0 || currentQ.type !== "MCQ"}
              className={`px-3 py-2 rounded ${
                lifelines.fifty > 0 ? "bg-gray-800 text-white" : "bg-gray-300"
              }`}
            >
              50-50 ({lifelines.fifty})
            </button>

            <button
              onClick={useSkip}
              disabled={lifelines.skip <= 0}
              className={`px-3 py-2 rounded ${
                lifelines.skip > 0 ? "bg-gray-800 text-white" : "bg-gray-300"
              }`}
            >
              Skip ({lifelines.skip})
            </button>

            <button
              onClick={useHint}
              disabled={lifelines.hint <= 0}
              className={`px-3 py-2 rounded ${
                lifelines.hint > 0 ? "bg-gray-800 text-white" : "bg-gray-300"
              }`}
            >
              Hint ({lifelines.hint})
            </button>
          </div>
        </div>

        {/* Power Ups */}
        <div className="bg-white p-4 rounded-2xl shadow">
          <div className="font-semibold mb-2">⚡ Power-ups</div>
          <button
            disabled={!canDrawPower}
            onClick={drawPower}
            className={`px-3 py-2 rounded ${
              canDrawPower
                ? "bg-purple-600 text-white"
                : "bg-gray-300 text-gray-500"
            }`}
          >
            Draw Power
          </button>

          {availablePower && (
            <div className="mt-2">
              <p className="text-sm">{availablePower.label}</p>
              <button
                onClick={activatePower}
                className="px-3 py-2 mt-2 bg-purple-600 text-white rounded"
              >
                Use Now
              </button>
            </div>
          )}

          {activePower && (
            <p className="text-purple-700 text-sm mt-2">
              Active: {activePower.label}
            </p>
          )}
        </div>

        {/* Multiplayer Stub */}
        <div className="bg-white p-4 rounded-2xl shadow">
          <div className="font-semibold mb-2">🆚 Multiplayer (Soon)</div>
          <button className="px-3 py-2 bg-gray-300 rounded text-gray-500">
            Create Room
          </button>
        </div>
      </div>

      {/* Finish Card */}
      {finished && (
        <div className="mt-6 bg-white p-6 rounded-2xl shadow">
          <h2 className="text-2xl font-bold">Game Finished 🎉</h2>
          <p className="mt-2">
            Points: <b>{points}</b> • Correct: <b>{totalCorrect}</b>
          </p>
          <p>
            Best Streak: <b>{bestStreak}</b> • Fastest:{" "}
            {fastestAnswerMs
              ? `${(fastestAnswerMs / 1000).toFixed(2)}s`
              : "–"}
          </p>

          <p className="mt-2">
            New Rank: <b>{rank}</b> • Total XP: <b>{xp}</b>
          </p>

          <div className="mt-4">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-green-600 text-white rounded"
            >
              Play Again
            </button>

            <Link
              to="/game"
              className="ml-3 px-4 py-2 bg-gray-800 text-white rounded"
            >
              Select Test
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Input Component
function NonMcqAnswer({ onSubmit }) {
  const [txt, setTxt] = useState("");

  return (
    <div className="mt-3">
      <input
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        placeholder="Type your answer"
        className="border rounded-lg px-3 py-2 w-full"
      />
      <button
        onClick={() => {
          onSubmit(txt);
          setTxt("");
        }}
        className="mt-2 px-4 py-2 bg-gray-800 text-white rounded"
      >
        Submit
      </button>
    </div>
  );
}
